import React, { useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';

import { FiCheck, FiCopy, FiCpu, FiPlus, FiTrash2 } from 'react-icons/fi';

import Input from './common/Input';
import Select from './common/Select';
import Button from './common/Button';
import ConnectionIndicator from './common/ConnectionIndicator';

import css from './SerialBoards.css';

const MAC_PATTERN = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/;

const lockTypeOptions = [
  { value: 'normalClose', label: 'Normalmente cerrada (NC)' },
  { value: 'normalOpen', label: 'Normalmente abierta (NO)' },
];

const lockTypeLabel = (value) => (lockTypeOptions.find((o) => o.value == value) || {}).label || '–';

const formatMac = (value) => {
  const hex = String(value == null ? '' : value)
    .toUpperCase()
    .replace(/[^0-9A-F]/g, '')
    .slice(0, 12);
  const pairs = hex.match(/.{1,2}/g);
  return pairs ? pairs.join(':') : '';
};

const toInt = (value) => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? null : parsed;
};

const toPayload = (boards) =>
  boards.map((board) => ({
    boardNumber: toInt(board.boardNumber),
    totalDoors: toInt(board.totalDoors),
    lockType: board.lockType,
    macAddress: board.macAddress,
  }));

const truncate = (text, max) => (text.length > max ? `${text.slice(0, max)}…` : text);

const newBoard = (boardNumber, totalDoors) => ({
  boardNumber,
  totalDoors,
  lockType: 'normalClose',
  macAddress: '',
});

const storageKey = (lockerId) => `mtk:serialBoards:${lockerId}`;

const readStored = (lockerId) => {
  try {
    const raw = window.localStorage.getItem(storageKey(lockerId));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
};

export const SerialBoards = ({ locker, readOnly, onBoardsChange }) => {
  const lockerId = locker.data.get('id');
  const serverBoards = locker.data.get('serialBoards');
  const initialBoards = useMemo(() => {
    const stored = readStored(lockerId);
    if (stored) return stored;
    return serverBoards ? serverBoards.toJS() : [];
  }, [lockerId, serverBoards]);

  const [boards, setBoards] = useState(initialBoards);
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setBoards(initialBoards);
    setDirty(false);
  }, [initialBoards]);

  const lockerDoors = locker.data.get('doors') ? locker.data.get('doors').size : 0;
  const assignedDoors = boards.reduce((sum, board) => sum + (toInt(board.totalDoors) || 0), 0);
  const remainingDoors = lockerDoors - assignedDoors;
  const usage = lockerDoors ? Math.min(100, Math.round((assignedDoors / lockerDoors) * 100)) : 0;
  const overCapacity = assignedDoors > lockerDoors;

  const boardErrors = boards.map((board, index) => {
    const errors = {};
    const boardNumber = toInt(board.boardNumber);
    const totalDoors = toInt(board.totalDoors);

    if (boardNumber == null || boardNumber < 1) errors.boardNumber = 'Número inválido';
    else if (boards.some((other, i) => i !== index && toInt(other.boardNumber) === boardNumber))
      errors.boardNumber = 'Número duplicado';

    if (totalDoors == null || totalDoors < 1) errors.totalDoors = 'Debe ser mayor a 0';

    if (!board.lockType) errors.lockType = 'Requerido';

    if (!MAC_PATTERN.test(board.macAddress || '')) errors.macAddress = 'Formato inválido (AA:BB:CC:DD:EE:FF)';
    else if (boards.some((other, i) => i !== index && other.macAddress === board.macAddress))
      errors.macAddress = 'MAC duplicada';

    return errors;
  });

  const hasFieldErrors = boardErrors.some((errors) => Object.keys(errors).length > 0);
  const canSave = !readOnly && dirty && !hasFieldErrors && !overCapacity;

  const updateBoard = (index, key, value) => {
    setBoards((prev) => prev.map((board, i) => (i === index ? { ...board, [key]: value } : board)));
    setDirty(true);
  };

  const onAddBoard = () => {
    const nextNumber = boards.reduce((max, board) => Math.max(max, toInt(board.boardNumber) || 0), 0) + 1;
    setBoards((prev) => [...prev, newBoard(nextNumber, Math.max(remainingDoors, 1))]);
    setDirty(true);
  };

  const onRemoveBoard = (index) => {
    setBoards((prev) => prev.filter((board, i) => i !== index));
    setDirty(true);
  };

  const onReset = () => {
    setBoards(initialBoards);
    setDirty(false);
  };

  const onSave = () => {
    const payload = toPayload(boards);
    window.localStorage.setItem(storageKey(lockerId), JSON.stringify(payload));
    if (onBoardsChange) onBoardsChange(payload);
    setBoards(payload);
    setDirty(false);
    setSavedAt(Date.now());
  };
  const onClearStored = () => {
    window.localStorage.removeItem(storageKey(lockerId));
    if (onBoardsChange) onBoardsChange(null);
    setBoards(serverBoards ? serverBoards.toJS() : []);
    setDirty(false);
    setSavedAt(null);
  };

  const payload = toPayload(boards);
  const json = JSON.stringify({ serialBoards: payload }, null, 2);
  const jsonPreview = truncate(JSON.stringify(payload), 64);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      window.prompt('Copia la configuración:', json);
    }
  };

  return (
    <div className={css.root}>
      <header className={css.summary}>
        <div className={css.summaryMain}>
          <span className={css.eyebrow}>Distribución de placas</span>
          <h2 className={css.title}>
            {assignedDoors} <span className={css.titleSep}>/</span> {lockerDoors}
            <span className={css.titleUnit}>puertas asignadas</span>
          </h2>
          <div className={css.meter}>
            <div
              className={classnames(css.meterFill, { [css.meterFillOver]: overCapacity })}
              style={{ width: `${usage}%` }}
            />
          </div>
        </div>
        <dl className={css.stats}>
          <div className={css.stat}>
            <dt>Placas</dt>
            <dd>{boards.length}</dd>
          </div>
          <div className={css.stat}>
            <dt>Disponibles</dt>
            <dd className={classnames({ [css.statOver]: overCapacity })}>{remainingDoors}</dd>
          </div>
        </dl>
        <details className={css.json}>
          <summary className={css.jsonSummary}>
            <span className={css.jsonLabel}>JSON</span>
            <code className={css.jsonPreview}>{jsonPreview}</code>
          </summary>
          <div className={css.jsonBody}>
            <pre className={css.jsonCode}>{json}</pre>
            <button type="button" className={css.copyButton} onClick={onCopy}>
              {copied ? <FiCheck /> : <FiCopy />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        </details>
      </header>

      {overCapacity && (
        <p className={css.alert}>
          La suma de puertas de las placas ({assignedDoors}) supera el total de puertas del locker ({lockerDoors}).
        </p>
      )}

      <p className={css.notice}>
        Configuración local: se guarda solo en este navegador y no se envía al locker.
        {savedAt ? ` Última vez guardada ${moment(savedAt).format('DD/MM/YYYY HH:mm')}.` : ''}
      </p>

      <div className={css.boards}>
        {boards.map((board, index) => {
          const errors = boardErrors[index];
          return (
            <article key={`board-${index}`} className={classnames(css.board, { [css.boardInvalid]: Object.keys(errors).length > 0 })}>
              <div className={css.boardHead}>
                <span className={css.boardBadge}>
                  <FiCpu />
                  Placa {toInt(board.boardNumber) || '?'}
                </span>
                <span className={css.boardMeta}>
                  {typeof board.online == 'boolean' && <ConnectionIndicator online={board.online} />}
                  {board.lastPing ? <span className={css.boardPing}>{moment(board.lastPing).fromNow()}</span> : null}
                </span>
                {!readOnly && (
                  <Button
                    small
                    invert
                    noMargin
                    className={css.removeButton}
                    confirm={`¿Eliminar la placa ${toInt(board.boardNumber) || ''}?`}
                    onClick={() => onRemoveBoard(index)}>
                    <FiTrash2 />
                  </Button>
                )}
              </div>
              <div className={css.boardGrid}>
                <Input
                  name={`board-${index}-boardNumber`}
                  type="number"
                  label="N° de placa"
                  className={css.field}
                  readOnly={readOnly}
                  value={board.boardNumber == null ? '' : board.boardNumber}
                  error={errors.boardNumber}
                  onChange={(name, value) => updateBoard(index, 'boardNumber', value)}
                />
                <Input
                  name={`board-${index}-totalDoors`}
                  type="number"
                  label="Puertas"
                  className={css.field}
                  readOnly={readOnly}
                  value={board.totalDoors == null ? '' : board.totalDoors}
                  error={errors.totalDoors}
                  onChange={(name, value) => updateBoard(index, 'totalDoors', value)}
                />
                <Select
                  name={`board-${index}-lockType`}
                  label="Tipo de cerradura"
                  className={css.field}
                  isClearable={false}
                  readOnly={readOnly}
                  options={lockTypeOptions}
                  value={board.lockType}
                  error={errors.lockType}
                  onChange={(name, value) => updateBoard(index, 'lockType', value)}
                />
                <Input
                  name={`board-${index}-macAddress`}
                  type="text"
                  label="Dirección MAC"
                  className={classnames(css.field, css.fieldMac)}
                  readOnly={readOnly}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  value={board.macAddress || ''}
                  error={errors.macAddress}
                  onChange={(name, value) => updateBoard(index, 'macAddress', formatMac(value))}
                />
              </div>
              <footer className={css.boardFoot}>
                {lockTypeLabel(board.lockType)} · {toInt(board.totalDoors) || 0} puertas
              </footer>
            </article>
          );
        })}

        {!boards.length && <p className={css.empty}>Este locker no tiene placas configuradas.</p>}
      </div>

      {!readOnly && (
        <div className={css.actions}>
          <Button
            noMargin
            onClick={onAddBoard}
            disabled={remainingDoors <= 0}
            title={remainingDoors <= 0 ? 'No quedan puertas disponibles' : undefined}>
            <FiPlus /> Agregar placa
          </Button>
          <div className={css.actionsRight}>
            {dirty && (
              <Button noMargin invert onClick={onReset}>
                Descartar
              </Button>
            )}
            <Button noMargin invert confirm="¿Borrar la configuración local?" onClick={onClearStored}>
              Limpiar
            </Button>
            <Button noMargin primary disabled={!canSave} onClick={onSave}>
              Guardar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SerialBoards;
